import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Droplets, Sun, Cloud, CloudRain, Sparkles, ChevronRight, ChevronLeft, Check, Target, Shield, Loader2 } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Skin Assessment - Multi-step questionnaire
export const SkinAssessment = ({ onComplete, onClose, initialSkinType = null }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1 = Skin Type, 2 = Concerns
  const [selectedType, setSelectedType] = useState(initialSkinType);
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch available skin concerns
  useEffect(() => {
    const fetchConcerns = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/skin-concerns`);
        setConcerns(response.data);
      } catch (error) {
        console.error("Failed to fetch concerns:", error);
        // Fallback concerns if API fails
        setConcerns([
          { id: 1, name: "Acne", description: "Breakouts, blackheads, whiteheads" },
          { id: 2, name: "Aging", description: "Fine lines, wrinkles, loss of firmness" },
          { id: 3, name: "Hyperpigmentation", description: "Dark spots, uneven skin tone" },
          { id: 4, name: "Sensitivity", description: "Easily irritated, redness, stinging" },
          { id: 5, name: "Dryness", description: "Tight feeling, flakiness" },
          { id: 6, name: "Dullness", description: "Lack of radiance, tired-looking skin" },
          { id: 7, name: "Large Pores", description: "Visible pores, textured skin" },
          { id: 8, name: "Redness", description: "Rosacea, flushing" }
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchConcerns();
  }, []);

  const skinTypes = [
    { 
      value: "dry", 
      label: "Dry", 
      icon: Sun,
      desc: "Often feels tight, may have flaky patches, fine lines more visible",
      color: "bg-amber-50 border-amber-200 hover:border-amber-400",
      selectedColor: "bg-amber-100 border-amber-500 ring-2 ring-amber-300"
    },
    { 
      value: "oily", 
      label: "Oily", 
      icon: CloudRain,
      desc: "Shiny appearance, enlarged pores, prone to breakouts",
      color: "bg-sky-50 border-sky-200 hover:border-sky-400",
      selectedColor: "bg-sky-100 border-sky-500 ring-2 ring-sky-300"
    },
    { 
      value: "combination", 
      label: "Combination", 
      icon: Cloud,
      desc: "Oily T-zone (forehead, nose, chin), dry or normal cheeks",
      color: "bg-violet-50 border-violet-200 hover:border-violet-400",
      selectedColor: "bg-violet-100 border-violet-500 ring-2 ring-violet-300"
    },
    { 
      value: "normal", 
      label: "Normal", 
      icon: Sparkles,
      desc: "Well-balanced, few imperfections, not too dry or oily",
      color: "bg-emerald-50 border-emerald-200 hover:border-emerald-400",
      selectedColor: "bg-emerald-100 border-emerald-500 ring-2 ring-emerald-300"
    },
    { 
      value: "sensitive", 
      label: "Sensitive", 
      icon: Shield,
      desc: "Reacts easily to products, may experience redness or irritation",
      color: "bg-rose-50 border-rose-200 hover:border-rose-400",
      selectedColor: "bg-rose-100 border-rose-500 ring-2 ring-rose-300"
    }
  ];

  const toggleConcern = (concernId) => {
    setSelectedConcerns(prev => {
      if (prev.includes(concernId)) {
        return prev.filter(id => id !== concernId);
      } else if (prev.length < 3) {
        return [...prev, concernId];
      }
      return prev; // Max 3 concerns
    });
  };

  const handleSave = async () => {
    if (!selectedType) {
      toast.error("Please select your skin type");
      return;
    }

    setSaving(true);
    try {
      // Save skin profile (type + concerns)
      await axios.put(
        `${API}/auth/skin-profile?skin_type=${selectedType}&${selectedConcerns.map(id => `concern_ids=${id}`).join('&')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Skin profile saved! Your routines will now be personalized.");
      onComplete({ skinType: selectedType, concerns: selectedConcerns });
    } catch (error) {
      console.error("Failed to save skin profile:", error);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getConcernIcon = (name) => {
    const icons = {
      "Acne": "🔴",
      "Aging": "⏳",
      "Hyperpigmentation": "🎯",
      "Sensitivity": "🌸",
      "Dryness": "🏜️",
      "Dullness": "✨",
      "Large Pores": "🔍",
      "Redness": "🌡️"
    };
    return icons[name] || "💧";
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl glass border-white/40" data-testid="skin-assessment">
        <DialogHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-pink-200/50 flex items-center justify-center mb-4">
            {step === 1 ? (
              <Droplets className="w-8 h-8 text-primary" />
            ) : (
              <Target className="w-8 h-8 text-primary" />
            )}
          </div>
          <DialogTitle className="font-serif text-2xl text-primary">
            {step === 1 ? "What's Your Skin Type?" : "What Are Your Skin Goals?"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {step === 1 
              ? "Understanding your skin type helps us recommend the right products"
              : "Select up to 3 concerns you'd like to address (optional)"}
          </DialogDescription>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-3 h-3 rounded-full transition-colors ${step >= 1 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className={`w-12 h-1 rounded ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
            <div className={`w-3 h-3 rounded-full transition-colors ${step >= 2 ? 'bg-primary' : 'bg-gray-200'}`} />
          </div>
        </DialogHeader>

        {/* Step 1: Skin Type */}
        {step === 1 && (
          <div className="grid gap-3 mt-6" data-testid="skin-type-step">
            {skinTypes.map(type => (
              <button
                key={type.value}
                onClick={() => setSelectedType(type.value)}
                className={`flex items-start gap-4 p-4 rounded-2xl border-2 text-left transition-all ${
                  selectedType === type.value ? type.selectedColor : type.color
                }`}
                data-testid={`select-skin-type-${type.value}`}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center">
                  <type.icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-primary text-lg">{type.label}</h3>
                    {selectedType === type.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{type.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Skin Concerns */}
        {step === 2 && (
          <div className="mt-6" data-testid="skin-concerns-step">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">
                    Selected: {selectedConcerns.length}/3
                  </p>
                  {selectedConcerns.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setSelectedConcerns([])}
                    >
                      Clear all
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {concerns.map(concern => {
                    const isSelected = selectedConcerns.includes(concern.id);
                    const isDisabled = !isSelected && selectedConcerns.length >= 3;
                    
                    return (
                      <button
                        key={concern.id}
                        onClick={() => toggleConcern(concern.id)}
                        disabled={isDisabled}
                        className={`flex flex-col items-start p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected 
                            ? 'bg-primary/10 border-primary ring-2 ring-primary/30' 
                            : isDisabled
                              ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                              : 'bg-white border-gray-200 hover:border-primary/50 hover:bg-primary/5'
                        }`}
                        data-testid={`concern-${concern.id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="text-xl">{getConcernIcon(concern.name)}</span>
                          <span className="font-medium text-primary flex-1">{concern.name}</span>
                          {isSelected && (
                            <Badge className="bg-primary text-white text-xs">
                              {selectedConcerns.indexOf(concern.id) + 1}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">{concern.description}</p>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t">
          {step === 1 ? (
            <>
              <Button variant="ghost" onClick={onClose} data-testid="skip-assessment-btn">
                Skip for now
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!selectedType}
                className="gap-2"
                data-testid="next-step-btn"
              >
                Next: Skin Goals
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep(1)} className="gap-2">
                <ChevronLeft className="w-4 h-4" />
                Back
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="gap-2 bg-gradient-to-r from-primary to-pink-500"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Save & Continue
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SkinAssessment;
